import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/hbs/Icon';
import { useOnboarding, type OnboardingStep } from './useOnboarding';
import { WelcomeModal } from './WelcomeModal';

function StepRow({
  step,
  onNavigate,
  onMarkExtensionInstalled,
}: {
  step: OnboardingStep;
  onNavigate: (to: string) => void;
  onMarkExtensionInstalled: () => void;
}): JSX.Element {
  return (
    <li className="flex items-start gap-3 py-2">
      <span
        aria-hidden
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
          step.done
            ? 'bg-hbs-primary border-hbs-primary text-hbs-on-primary'
            : 'border-hbs-border text-transparent'
        }`}
      >
        <Icon name="check" size={12} />
      </span>
      <div className="flex-1 min-w-0">
        <p className={`text-[13.5px] font-semibold ${step.done ? 'text-hbs-text-mute line-through' : 'text-hbs-text'}`}>
          {step.title}
        </p>
        <p className="text-[12px] text-hbs-text-mute mt-0.5">{step.description}</p>
        {!step.done && (
          <div className="mt-1.5 flex items-center gap-3">
            {step.to && (
              <button
                type="button"
          id={`onboarding-step-${step.id}`}
          onClick={() => onNavigate(step.to!)}
                className="text-[12.5px] font-semibold text-hbs-primary hover:underline"
              >
                {step.cta}
              </button>
            )}
            {step.href && (
              <a
                href={step.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-hbs-primary hover:underline"
              >
                {step.cta}
                <Icon name="launch" size={12} />
              </a>
            )}
            {/* The extension step is the one we can't observe — let the user check it off. */}
            {step.id === 'installExtension' && (
              <button
                type="button"
          id="onboarding-mark-extension-installed-button"
          onClick={onMarkExtensionInstalled}
                className="text-[12.5px] text-hbs-text-mute hover:text-hbs-text hover:underline"
              >
                Mark installed
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * First-run onboarding surface: a one-time welcome modal plus a persistent
 * getting-started checklist pinned to the top of the safe list. Calls
 * `useOnboarding` once so the modal and checklist share state (a skip from the
 * modal correctly hides the checklist too). Renders nothing once onboarding is
 * complete or dismissed.
 */
export function OnboardingChecklist(): JSX.Element | null {
  const navigate = useNavigate();
  const {
    visible,
    showWelcome,
    steps,
    completedCount,
    total,
    markWelcomeSeen,
    dismiss,
    skipAll,
    markExtensionInstalled,
  } = useOnboarding();

  if (!visible && !showWelcome) return null;

  return (
    <>
      <WelcomeModal open={showWelcome} onGetStarted={markWelcomeSeen} onSkip={skipAll} />
      {visible && (
        <section
          aria-label="Getting started"
          className="mb-2 rounded-hbs-xl border border-hbs-border bg-hbs-surface p-4"
        >
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <h2 className="font-display text-[15px] tracking-hbs-tight text-hbs-text">Getting started</h2>
              <p className="text-[12px] text-hbs-text-mute mt-0.5">
                {completedCount} of {total} done
              </p>
            </div>
            <button
              type="button"
              aria-label="Dismiss getting started"
        id="onboarding-dismiss-button"
        onClick={dismiss}
              className="shrink-0 p-1 rounded-hbs-sm text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt"
            >
              ×
            </button>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-hbs-surface-alt">
            <div
              className="h-full rounded-full bg-hbs-primary transition-all"
              style={{ width: `${total ? (completedCount / total) * 100 : 0}%` }}
            />
          </div>
          <ul className="mt-2 divide-y divide-hbs-border">
            {steps.map((step) => (
              <StepRow
                key={step.id}
                step={step}
                onNavigate={navigate}
                onMarkExtensionInstalled={markExtensionInstalled}
              />
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
